import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    CriterionDataValue,
    GenericSelectionCriterionData,
    SalesHistorySelectionCriterionData,
    SelectionCriteriaData,
    SelectionCriterion,
    SelectionCriterionType,
    WebActivitySelectionCriterionData,
    WebSearchSelectionCriterionData,
} from '@r1-compass-library/models/criteria/selection-criteria.model';
import { Uuid } from '@r1-compass-library/utilities/uuid';
import { DataMartField, DataType, FieldType } from '../public-api';

export class SelectionCriterionForm {
    id = new FormControl<string>(Uuid.generate(), {
        nonNullable: true,
    });
    type = new FormControl<SelectionCriterionType | undefined>(undefined, {
        nonNullable: true,
    });
    query = new FormControl<string | undefined>(undefined, {
        nonNullable: true,
    });

    // The `no-restricted-syntax` rules are suppressed in this instance because the current
    // approach can only instantiate one type. However, this is not an issue upstream, as the
    // end form group will always be patched after this step with the correct data, not mutating
    // the actual class member form
    data:
        | FormGroup<GenericSelectionCriterionForm>
        | FormGroup<WebSearchSelectionCriterionDataForm>
        | FormGroup<WebActivitySelectionCriterionDataForm>
        | FormGroup<SalesHistorySelectionCriterionDataForm>
        | FormGroup<undefined> = new FormGroup(new GenericSelectionCriterionForm());

    constructor(criterionType?: SelectionCriterionType, selectionCriterion?: SelectionCriterion) {
        this.id.setValue(selectionCriterion?.id ?? this.id.value);
        this.type.setValue(criterionType);
        this.query.setValue(selectionCriterion?.query);
        if (criterionType === 'generic' || criterionType === 'model') {
            // eslint-disable-next-line no-restricted-syntax
            this.data = new FormGroup(
                new GenericSelectionCriterionForm(
                    selectionCriterion?.data as GenericSelectionCriterionData,
                    criterionType
                )
            );
        } else if (criterionType === 'web activity') {
            // eslint-disable-next-line no-restricted-syntax
            this.data = new FormGroup(
                new WebActivitySelectionCriterionDataForm(selectionCriterion?.data as WebActivitySelectionCriterionData)
            );
        } else if (criterionType === 'web search') {
            // eslint-disable-next-line no-restricted-syntax
            this.data = new FormGroup(
                new WebSearchSelectionCriterionDataForm(selectionCriterion?.data as WebSearchSelectionCriterionData)
            );
        } else if (criterionType === 'sales history') {
            // eslint-disable-next-line no-restricted-syntax
            this.data = new FormGroup(
                new SalesHistorySelectionCriterionDataForm(
                    selectionCriterion?.data as SalesHistorySelectionCriterionData
                )
            );
        }
    }
}

export class GenericSelectionCriterionForm {
    id = new FormControl<string | undefined>(Uuid.generate(), { nonNullable: true });
    type = new FormControl<SelectionCriterionType>('generic', { nonNullable: true });
    tableName = new FormControl<string | undefined>(undefined, { nonNullable: true, validators: Validators.required });
    dataMartFieldName = new FormControl<string | undefined>(undefined, {
        nonNullable: true,
        validators: Validators.required,
    });
    not = new FormControl<boolean | null>(null, { nonNullable: true });
    comparator = new FormControl<string | null>(null, { nonNullable: true, validators: Validators.required });
    value1 = new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm());
    value2 = new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm());

    constructor(data?: GenericSelectionCriterionData, type: SelectionCriterionType = 'generic') {
        this.type.setValue(type);
        if (data) {
            this.id.setValue(data.id || Uuid.generate());
            this.tableName.setValue(data.dataMartField?.tableName);
            this.dataMartFieldName.setValue(data.dataMartField?.name || undefined);
            if (data?.not === true || data?.not === false) {
                this.not.setValue(data.not);
            } else {
                this.not.setValue(null);
            }
            this.comparator.setValue(data.comparator || null);
            this.value1.patchValue(
                new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm(data.value1)).getRawValue()
            );
            this.value2.patchValue(
                new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm(data.value2)).getRawValue()
            );
        }
    }
}

export class CriterionDataValueForm {
    type = new FormControl<string>('', { nonNullable: true });
    displayType = new FormControl<string>('', { nonNullable: true });
    value = new FormControl<string | null>(null, { nonNullable: true, validators: [Validators.required] }); // needs conversion upon saving

    constructor(data?: CriterionDataValue) {
        this.type.setValue(data?.type ?? '');
        this.displayType.setValue(data?.displayType ?? '');

        if (data?.value) {
            const nonNullValues = data.value.filter(val => val !== null);
            if (nonNullValues.length === 0) {
                this.value.setValue(null);
            } else {
                this.value.setValue(data?.value.join(',') ?? '');
            }
        } else {
            this.value.setValue(null);
        }
    }
}

export class WebSearchSelectionCriterionDataForm {
    type = new FormControl<SelectionCriterionType>('web search', { nonNullable: true });
    searchTerm = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
    startDate = new FormControl<string | null>(null, { nonNullable: true });
    endDate = new FormControl<string | null>(null, { nonNullable: true });

    constructor(data?: WebSearchSelectionCriterionData) {
        this.searchTerm.setValue(data?.searchTerm ?? '');
        this.startDate.setValue(data?.startDate ?? null);
        this.endDate.setValue(data?.endDate ?? null);
    }
}

export class WebActivitySelectionCriterionDataForm {
    type = new FormControl<SelectionCriterionType>('web activity', { nonNullable: true });
    cmtLevel = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
    cmtCategory = new FormControl<string>('', { nonNullable: true });
    not = new FormControl<boolean | null>(null, { nonNullable: true });
    comparator = new FormControl<string | null>(null, {
        nonNullable: true,
        validators: [Validators.required],
    });
    webActivityPeriod = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
    value1 = new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm());

    constructor(data?: WebActivitySelectionCriterionData) {
        this.cmtLevel.setValue(data?.cmtLevel ?? '');
        this.cmtCategory.setValue(data?.cmtCategory ?? '');
        if (data?.not === true || data?.not === false) {
            this.not.setValue(data.not);
        } else {
            this.not.setValue(null);
        }
        this.comparator.setValue(data?.comparator ?? null);
        this.webActivityPeriod.setValue(data?.webActivityPeriod ?? '');
        this.value1.patchValue(
            new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm(data?.value1)).getRawValue()
        );
    }
}

export class SalesHistorySelectionCriterionDataForm {
    type = new FormControl<SelectionCriterionType>('sales history', { nonNullable: true });
    cmtLevel = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
    cmtCategory = new FormControl<string>('', { nonNullable: true });
    not = new FormControl<boolean | null>(null, { nonNullable: true });
    metric = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
    channel = new FormControl<string[]>([], { nonNullable: true });
    comparator = new FormControl<string | null>(null, {
        nonNullable: true,
        validators: [Validators.required],
    });
    value1 = new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm());
    isHandPriced = new FormControl<string>('', { nonNullable: true });
    vendors = new FormControl<string[]>([], { nonNullable: true });
    selfManufactured = new FormControl<string[]>([], { nonNullable: true });
    ssp = new FormControl<string[]>([], { nonNullable: true });
    privateLabel = new FormControl<string[]>([], { nonNullable: true });

    constructor(data?: SalesHistorySelectionCriterionData) {
        this.cmtLevel.setValue(data?.cmtLevel ?? '');
        this.cmtCategory.setValue(data?.cmtCategory ?? '');
        if (data?.not === true || data?.not === false) {
            this.not.setValue(data.not);
        } else {
            this.not.setValue(null);
        }
        this.metric.setValue(data?.metric ?? '');
        this.channel.setValue(data?.channel ?? []);
        this.comparator.setValue(data?.comparator ?? null);
        this.value1.patchValue(
            new FormGroup<CriterionDataValueForm>(new CriterionDataValueForm(data?.value1)).getRawValue()
        );
        this.isHandPriced.setValue(data?.isHandPriced ?? '');
        this.vendors.setValue(data?.vendors ?? []);
        this.selfManufactured.setValue(data?.selfManufactured ?? []);
        this.ssp.setValue(data?.ssp ?? []);
        this.privateLabel.setValue(data?.privateLabel ?? []);
    }
}

export function convertSelectionCriterionForm(
    form: FormGroup<SelectionCriterionForm>,
    dataMartFields: DataMartField[]
): SelectionCriterion {
    let data: SelectionCriteriaData;
    switch (form.controls.type.value) {
        case 'generic':
            data = convertGenericSelectionCriterionForm(
                form.controls.data as FormGroup<GenericSelectionCriterionForm>,
                dataMartFields
            );
            break;
        case 'model':
            data = convertGenericSelectionCriterionForm(
                form.controls.data as FormGroup<GenericSelectionCriterionForm>,
                dataMartFields
            );
            break;
        case 'web activity':
            data = convertWebActivitySelectionCriterionForm(
                form.controls.data as FormGroup<WebActivitySelectionCriterionDataForm>
            );
            break;
        case 'sales history':
            data = convertSalesHistorySelectionCriterionForm(
                form.controls.data as FormGroup<SalesHistorySelectionCriterionDataForm>
            );
            break;
        default:
            data = convertWebSeasrchSelectionCriterionForm(
                form.controls.data as FormGroup<WebSearchSelectionCriterionDataForm>
            );
    }

    return {
        id: form.controls.id.value,
        query: form.controls.query.value,
        type: form.controls.type.value,
        data: data,
    };
}

export function convertGenericSelectionCriterionForm(
    form: FormGroup<GenericSelectionCriterionForm>,
    fields: DataMartField[]
): GenericSelectionCriterionData {
    const dataMartFieldToSave = fields.find(field => field.name === form.controls.dataMartFieldName.value);
    if (dataMartFieldToSave?.dataType) {
        dataMartFieldToSave.dataType = dataMartFieldToSave?.dataType.toUpperCase() as DataType;
    }
    if (dataMartFieldToSave?.fieldType) {
        dataMartFieldToSave.fieldType = dataMartFieldToSave?.fieldType.toUpperCase() as FieldType;
    }
    if (dataMartFieldToSave) {
        dataMartFieldToSave.tableName = form.controls.tableName.value;
    }
    return {
        id: form.controls.id.value,
        not: form.controls.not.value,
        comparator: form.controls.comparator.value,
        dataMartField: dataMartFieldToSave,
        value1: convertCriterionDataValueForm(form.controls.value1),
        value2: convertCriterionDataValueForm(form.controls.value2),
    };
}

export function convertWebSeasrchSelectionCriterionForm(
    form: FormGroup<WebSearchSelectionCriterionDataForm>
): WebSearchSelectionCriterionData {
    return form.getRawValue();
}

export function convertWebActivitySelectionCriterionForm(
    form: FormGroup<WebActivitySelectionCriterionDataForm>
): WebActivitySelectionCriterionData {
    return {
        cmtCategory: form.controls.cmtCategory.value,
        cmtLevel: form.controls.cmtLevel.value,
        not: form.controls.not.value,
        comparator: form.controls.comparator.value,
        webActivityPeriod: form.controls.webActivityPeriod.value,
        value1: convertCriterionDataValueForm(form.controls.value1),
    };
}

export function convertSalesHistorySelectionCriterionForm(
    form: FormGroup<SalesHistorySelectionCriterionDataForm>
): SalesHistorySelectionCriterionData {
    return {
        cmtLevel: form.controls.cmtLevel.value,
        cmtCategory: form.controls.cmtCategory.value,
        not: form.controls.not.value,
        metric: form.controls.metric.value,
        channel: form.controls.channel.value,
        comparator: form.controls.comparator.value,
        value1: convertCriterionDataValueForm(form.controls.value1),
        isHandPriced: form.controls.isHandPriced.value,
        vendors: form.controls.vendors.value,
        selfManufactured: form.controls.selfManufactured.value,
        ssp: form.controls.ssp.value,
        privateLabel: form.controls.privateLabel.value,
    };
}

export function convertCriterionDataValueForm(form: FormGroup<CriterionDataValueForm>): CriterionDataValue {
    let value = form.controls.value.value;
    if (!value) {
        value = null;
    }
    return {
        type: form.controls.type.value,
        displayType: form.controls.displayType.value,
        value: [value],
    };
}
